import { useMemo } from 'react';
import { TFunction } from 'i18next';

import {
  BoardMineralization,
  BoardSoil,
  BoardStatus,
} from 'features/Croptimizer/types';
import { numericValues } from 'utils/helpers';
import { getEfficiencyFormula } from 'features/Croptimizer/utils/calcHelperBoard';

interface Props {
  getY2CurrentProfit?: number | string;
  mineralizationValues: BoardMineralization | null;
  boardStatusValues: BoardStatus | null;
  boardSoilValues: BoardSoil | null;
  optimumValue: number;
  t: TFunction;
}

const Y3CurrentProfitFirst = ({
  getY2CurrentProfit,
  boardStatusValues,
  mineralizationValues,
  boardSoilValues,
  optimumValue,
  t,
}: Props) => {
  const result = useMemo(() => {
    if (
      getY2CurrentProfit &&
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues
    ) {
      const getSec = (
        ((Math.tan((Number(getY2CurrentProfit) - 5.8) / 1.45) * 54 + 70) /
          100 /
          0.05) *
        5
      ).toFixed(2);

      const ag38 = getEfficiencyFormula(
        Number(mineralizationValues?.month),
        Number(boardStatusValues?.currentPh),
        Number(numericValues(boardStatusValues?.currentSec)),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );
      const ah38 = getEfficiencyFormula(
        24,
        Number(getY2CurrentProfit),
        Number(getSec),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );
      const ai38 = getEfficiencyFormula(
        36,
        Number(getY2CurrentProfit),
        Number(getSec),
        // Number(boardSoilValues?.selected),
        optimumValue,
        Number(numericValues(mineralizationValues?.soilTillage)),
      );

      const ag31 = ag38;
      const ah31 = ah38 < ag31 ? ag31 : ah38;
      const ai78 = ah31 / 100;
      const ah78 = ag31 / 100;
      const ah79 = ag31 / 100;
      const ai71 = ai78 - ah78;
      const ai64 = ai71 / Number(boardSoilValues?.split);
      const ah71 = ah78;
      const ah64 = ah71 / Number(boardSoilValues?.split);
      const dose = Number(numericValues(boardSoilValues?.dose)) / 100;
      const ai65 = (ah78 + ai71) / Number(boardSoilValues?.split);
      const ai79 = ah31 / 100;
      const ai31 = ai38 < ah31 ? ah31 : ai38;
      const aj78 = ai31 / 100;
      const aj71 = aj78 - ai78;
      const aj64 = aj71 / Number(boardSoilValues?.split);
      const aj79 = ai31 / 100;
      const aj72 = aj79 - ai79;
      const ai72 = ai79 - ah79;
      const aj65 = (aj72 + ai72) / Number(boardSoilValues?.split);
      const aj66 = (aj72 + ai72 + ah78) / Number(boardSoilValues?.split);

      if (mineralizationValues?.cap === t('no')) {
        if (boardSoilValues?.split === '1') {
          return (
            Number(getY2CurrentProfit) -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '2') {
          return (
            Number(getY2CurrentProfit) -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '3') {
          const aa66 = Number(getY2CurrentProfit);
          const phcenter =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const centerDose = dose / Number(boardSoilValues?.split);

          return (
            aa66 +
            phcenter * centerDose -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '4') {
          const aa66 = Number(getY2CurrentProfit);
          const phcenter =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const centerDose = dose / Number(boardSoilValues?.split);

          return (
            aa66 +
            phcenter * centerDose -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }

        if (boardSoilValues?.split === '5') {
          const aa66 = Number(getY2CurrentProfit);
          const phcenter =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const centerDose = dose / Number(boardSoilValues?.split);

          return (
            aa66 +
            phcenter * centerDose -
            Number(mineralizationValues?.annualDecrease)
          ).toFixed(2);
        }
      } else {
        if (boardSoilValues?.split === '1') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah64 + ai64 + aj64;
          const fourth = Number(mineralizationValues?.annualDecrease) * 3;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '2') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah64 + ai65 + aj65;
          const fourth = Number(mineralizationValues?.annualDecrease) * 3;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '3') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah64 + ai65 + aj66;
          const fourth = Number(mineralizationValues?.annualDecrease) * 3;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '4') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah64 + ai65 + aj66;
          const fourth = Number(mineralizationValues?.annualDecrease) * 3;

          return (first + second * third - fourth).toFixed(2);
        }

        if (boardSoilValues?.split === '5') {
          const first = Number(boardStatusValues?.currentPh);
          const second =
            Number(boardStatusValues?.targetPh) -
            Number(boardStatusValues?.currentPh);
          const third = ah64 + ai65 + aj66;
          const fourth = Number(mineralizationValues?.annualDecrease) * 3;

          return (first + second * third - fourth).toFixed(2);
        }
      }
    }
  }, [
    getY2CurrentProfit,
    boardStatusValues,
    mineralizationValues,
    boardSoilValues,
    boardSoilValues?.split,
  ]);

  return { result };
};

export default Y3CurrentProfitFirst;
